import { useEffect, useRef, useState } from "react";
import { Input } from "@/components/ui/input";
import { suggestInci } from "@/lib/api";

export default function INCIAutocomplete({ value, onChange, placeholder, testId = "inp-inci", className = "rounded-none h-9 font-mono text-sm" }) {
  const [open, setOpen] = useState(false);
  const [items, setItems] = useState([]);
  const [highlight, setHighlight] = useState(-1);
  const box = useRef(null);
  const timer = useRef(null);

  useEffect(() => {
    if (!open) return;
    if (timer.current) clearTimeout(timer.current);
    timer.current = setTimeout(async () => {
      try {
        const d = await suggestInci(value || "");
        setItems(d.results || []);
        setHighlight(-1);
      } catch { /* empty */ }
    }, 120);
    return () => timer.current && clearTimeout(timer.current);
  }, [value, open]);

  useEffect(() => {
    const onClick = (e) => { if (box.current && !box.current.contains(e.target)) setOpen(false); };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const pick = (v) => { onChange(v); setOpen(false); };

  const onKey = (e) => {
    if (!open) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setHighlight((h) => Math.min(items.length - 1, h + 1)); }
    else if (e.key === "ArrowUp") { e.preventDefault(); setHighlight((h) => Math.max(-1, h - 1)); }
    else if (e.key === "Enter" && highlight >= 0) { e.preventDefault(); pick(items[highlight]); }
    else if (e.key === "Escape") setOpen(false);
  };

  return (
    <div className="relative" ref={box}>
      <Input
        value={value || ""}
        onChange={(e) => { onChange(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onKeyDown={onKey}
        placeholder={placeholder}
        className={className}
        data-testid={testId}
        autoComplete="off"
      />
      {open && items.length > 0 && (
        <div
          className="absolute left-0 right-0 top-full mt-1 z-50 border border-[var(--border)] bg-white max-h-60 overflow-auto"
          data-testid={`${testId}-dropdown`}
        >
          {items.map((n, i) => (
            <button
              key={n}
              type="button"
              onMouseEnter={() => setHighlight(i)}
              onMouseDown={(e) => { e.preventDefault(); pick(n); }}
              className={`block w-full text-left px-3 py-2 font-mono text-xs cell-border last:border-b-0 ${i === highlight ? "bg-[var(--surface)] text-[var(--primary)]" : "hover:bg-[var(--surface)]"}`}
              data-testid={`${testId}-option-${i}`}
            >
              {n}
            </button>
          ))}
        </div>
      )}
    </div>
  );
}
